import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Car, Download, User, MapPin, ChevronLeft, ChevronRight, Zap, Navigation, CreditCard } from 'lucide-react';

const SCREENS = [
  { id: 1, image: '/images/homepage.jpg', title: 'Home Screen', icon: Zap, accent: 'primary', desc: 'Your entire ride dashboard at a glance.' },
  { id: 2, image: '/images/book-a-ride.jpeg', title: 'Book a Ride', icon: MapPin, accent: 'teal', desc: 'Pin pickup and drop, confirm. Done in seconds.' },
  { id: 3, image: '/images/live-tracking.jpeg', title: 'Live Tracking', icon: Navigation, accent: 'accent', desc: 'Know exactly where your ride is, every moment.' },
  { id: 4, image: '/images/direct-payment.jpeg', title: 'Direct Payment', icon: CreditCard, accent: 'ink', desc: 'Pay the driver directly. No platform fees.' },
];

const COLOR_CLASSES = {
  teal: { text: 'text-teal-dark dark:text-teal', solid: 'bg-teal-dark', tint: 'bg-teal-tint dark:bg-teal/10' },
  primary: { text: 'text-primary', solid: 'bg-primary', tint: 'bg-primary-tint dark:bg-primary/10' },
  accent: { text: 'text-accent-dark dark:text-accent', solid: 'bg-accent-dark', tint: 'bg-accent-tint dark:bg-accent/10' },
  ink: { text: 'text-ink dark:text-dark-ink', solid: 'bg-ink', tint: 'bg-surface-alt dark:bg-white/10' },
};

const PhoneFrame = ({ screen }) => (
  <div className="w-[230px] shrink-0 rounded-[2.5rem] border-[3px] border-ink dark:border-dark-line bg-ink p-2 shadow-xl">
    <div className="rounded-[2rem] overflow-hidden bg-white h-[460px] relative">
      <AnimatePresence mode="wait">
        <motion.img
          key={screen.id}
          src={screen.image}
          alt={screen.title}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full h-full object-cover block"
        />
      </AnimatePresence>
    </div>
  </div>
);

const AppPreview = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((p) => (p + 1) % SCREENS.length), 3800);
    return () => clearInterval(id);
  }, []);

  const screen = SCREENS[active];
  const c = COLOR_CLASSES[screen.accent];

  return (
    <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
      <div className="max-w-[1200px] mx-auto">

        <div className="text-center max-w-lg mx-auto mb-8">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4 bg-primary-tint dark:bg-primary/10 border border-primary/25 text-primary">
            App Preview
          </span>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight text-ink dark:text-dark-ink mb-3">
            See it in action
          </h2>
          <p className="text-base text-ink-soft dark:text-dark-ink-soft">
            Book, ride, pay. That's it.
          </p>
        </div>

        <div className="flex flex-col items-center gap-6">
          <PhoneFrame screen={screen} />

          <AnimatePresence mode="wait">
            <motion.div
              key={screen.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className={`flex items-start gap-2.5 px-4 py-3 rounded-control max-w-xs text-left ${c.tint}`}
            >
              <screen.icon size={14} className={`${c.text} shrink-0 mt-0.5`} aria-hidden="true" />
              <p className="text-sm leading-snug">
                <span className={`font-semibold ${c.text}`}>{screen.title}</span>
                <span className="text-ink-soft"> · {screen.desc}</span>
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setActive((p) => (p - 1 + SCREENS.length) % SCREENS.length)}
              className="w-8 h-8 rounded-full border border-line dark:border-dark-line bg-white dark:bg-dark-surface flex items-center justify-center"
              aria-label="Previous screen"
            >
              <ChevronLeft size={14} className="text-ink-muted" />
            </button>
            <div className="flex gap-2">
              {SCREENS.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setActive(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${i === active ? COLOR_CLASSES[s.accent].solid : 'bg-line dark:bg-dark-line'}`}
                  aria-label={`View ${s.title}`}
                />
              ))}
            </div>
            <button
              onClick={() => setActive((p) => (p + 1) % SCREENS.length)}
              className="w-8 h-8 rounded-full border border-line dark:border-dark-line bg-white dark:bg-dark-surface flex items-center justify-center"
              aria-label="Next screen"
            >
              <ChevronRight size={14} className="text-ink-muted" />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-2">
            <a
              href="https://play.google.com/store/apps/details?id=com.elt.totowala"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white bg-primary hover:bg-primary-dark transition-colors"
            >
              <User size={15} /> Download for Riders <Download size={14} className="opacity-75" />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.elt.totowalafordrivers"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white bg-teal-dark hover:bg-teal transition-colors"
            >
              <Car size={15} /> Download for Drivers <Download size={14} className="opacity-75" />
            </a>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AppPreview;
